/**
 * HandoffHistoryTimeline
 * Renders a handoff's lifecycle events (SPEC-2253) as a compact vertical
 * timeline, oldest-first. Renders nothing when there are no events, so legacy
 * handoffs (empty history) show no timeline.
 */
import { FC } from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { formatDate } from '../../../../utils/dateTime';
import { resolveAssignedAgent, formatAssignedAgentName } from '../../../../utils/assignedAgent';
import type { FormattedUser } from '../../../../hooks/graphql/users/useOrganizationUsers';
import type { HandoffLifecycleEvent } from '../../../../api/handoff';
import { humanizeEvent, resolveEventLabel } from '../../../../utils/handoffTimeline';

// Green = agent committed / progressed (claim variants, dial, release, convert);
// red = failure/expiry; everything else — soft hold, soft-hold expiry, give-back
// — defaults to yellow.
const EVENT_DOT: Record<string, string> = {
  claimed: 'bg-green-500',
  claim_link_viewed: 'bg-amber-500',
  hard_accepted: 'bg-green-500',
  hard_claimed: 'bg-green-500',
  released: 'bg-green-500',
  given_back: 'bg-green-500',
  converted: 'bg-green-500',
  expired: 'bg-red-500',
  hard_expired: 'bg-red-500',
  declined: 'bg-red-500',
  decline_link_used: 'bg-red-500',
};
const DEFAULT_DOT = 'bg-amber-500';

// Backend denormalizes actorAgentName inconsistently (e.g. present on
// claim_link_used but not call_initiated). Fill the gaps from other events in
// the same history that DO carry the name for that actorAgentId.
const buildAgentNameById = (events: HandoffLifecycleEvent[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const ev of events) {
    if (ev.actorAgentId && ev.actorAgentName) map.set(ev.actorAgentId, ev.actorAgentName);
  }
  return map;
};

const actorLabel = (
  ev: HandoffLifecycleEvent,
  agentNameById: Map<string, string>,
  orgUsers: FormattedUser[]
): string => {
  switch (ev.actorType) {
    case 'agent': {
      if (ev.actorAgentName) return ev.actorAgentName;
      if (ev.actorAgentId) {
        // Reliable: resolve the name from org users, same as the assigned-agent
        // cell. Fall back to another event in this history while users load.
        const fromUsers = formatAssignedAgentName(
          resolveAssignedAgent(null, ev.actorAgentId, orgUsers)
        );
        if (fromUsers) return fromUsers;
        const fromHistory = agentNameById.get(ev.actorAgentId);
        if (fromHistory) return fromHistory;
      }
      return 'Agent';
    }
    case 'link':
      return 'Claim link';
    case 'scheduler':
      return 'Scheduler';
    default:
      return 'System';
  }
};

interface HandoffHistoryTimelineProps {
  events: HandoffLifecycleEvent[];
  orgTimezone?: string;
  orgUsers?: FormattedUser[];
}

export const HandoffHistoryTimeline: FC<HandoffHistoryTimelineProps> = ({
  events,
  orgTimezone,
  orgUsers = [],
}) => {
  if (!events?.length) return null;

  const agentNameById = buildAgentNameById(events);

  return (
    <ol className="space-y-0">
      {events.map((ev, i) => {
        const isLast = i === events.length - 1;
        const dotColor = EVENT_DOT[ev.event] ?? DEFAULT_DOT;
        return (
          <li key={`${ev.event}-${ev.at}-${i}`} className="flex gap-2.5 sm:gap-3">
            <div className="flex flex-col items-center">
              <span className="relative mt-1 flex h-2.5 w-2.5 shrink-0 items-center justify-center">
                <span
                  className={cn('absolute inset-0 rounded-full opacity-30 blur-[2px]', dotColor)}
                />
                <span className={cn('relative h-2 w-2 rounded-full shadow-sm', dotColor)} />
              </span>
              {!isLast && (
                <span className="mt-1 w-px flex-1 rounded-full bg-gradient-to-b from-foreground/20 to-foreground/5" />
              )}
            </div>
            <div className={cn('min-w-0 flex-1', isLast ? 'pb-0.5' : 'pb-3 sm:pb-4')}>
              <div className="flex items-baseline justify-between gap-2 sm:gap-3">
                <span className="text-[13px] sm:text-sm font-semibold leading-tight tracking-tight text-foreground">
                  {resolveEventLabel(ev)}
                </span>
                <span className="shrink-0 text-[10.5px] sm:text-xs font-medium tabular-nums text-muted-foreground">
                  {/* Drop the timezone on mobile to keep the row from wrapping. */}
                  <span className="sm:hidden">{formatDate(ev.at, orgTimezone, 'MMM D, h:mm A')}</span>
                  <span className="hidden sm:inline">
                    {formatDate(ev.at, orgTimezone, 'MMM D, h:mm A z')}
                  </span>
                </span>
              </div>
              <div className="mt-0.5 sm:mt-1 flex items-center gap-1.5 text-[11px] sm:text-xs text-muted-foreground">
                <span className="truncate">{actorLabel(ev, agentNameById, orgUsers)}</span>
                {ev.fromStatus && ev.toStatus && (
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground/50">·</span>
                    <span className="truncate">{humanizeEvent(ev.fromStatus)}</span>
                    <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground/60" />
                    <span className="truncate">{humanizeEvent(ev.toStatus)}</span>
                  </span>
                )}
              </div>
              {/* Only what an agent actually wrote. `reason` also lands on the
                  event, but it is the machine label the frontend sent with the
                  action — "Declined in app" under an entry already titled
                  "Declined (In-App)" is the event name twice. */}
              {ev.notes && (
                <div className="mt-1 sm:mt-1.5 break-words border-l-2 border-foreground/25 pl-2.5 text-[11px] sm:text-xs leading-relaxed text-foreground/80">
                  <span className="mr-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Note
                  </span>
                  {ev.notes}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default HandoffHistoryTimeline;
